import { useState, useEffect, useMemo, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilter,
  faSearch,
  faPlus,
  faXmark,
} from "@fortawesome/sharp-regular-svg-icons";
import classNames from "classnames";
import { debounce } from "lodash";

import { AppealCurrentStatus } from "@/__generated__/graphql";
import { Button } from "../buttons/button/button";
import { StatusDropdown } from "../status-dropdown/status-dropdown";
import { DateRangeFilter } from "../date-range-filter";
import { SearchModal } from "../search-modal";
import { SearchChip } from "../search-chip";
import { SearchCriteria } from "../../types/search";

import styles from "./search-bar.module.scss";

export interface SearchConfig {
  onSearch: (criteria: SearchCriteria) => void;
  onClear: () => void;
  criteria: SearchCriteria;
  hasActiveSearch: boolean;
}

export interface FilterConfig {
  onStatusChange: (statuses: string[]) => void;
  onReset: () => void;
  selectedStatuses?: string[];
  showReferredByMe: boolean;
  onReferredByMeChange: (isActive: boolean) => void;
  dateRange: [string | null, string | null];
  onDateRangeChange: (range: [string | null, string | null]) => void;
  showRecentOutcomes: boolean;
  onRecentOutcomesChange: (isActive: boolean) => void;
}

interface SearchBarProps {
  className?: string;
  search: SearchConfig;
  filters: FilterConfig;
}

export const SearchBar = ({ className, search, filters }: SearchBarProps) => {
  const {
    onStatusChange,
    onReset,
    selectedStatuses: controlledSelectedStatuses,
    showReferredByMe,
    onReferredByMeChange,
    dateRange,
    onDateRangeChange,
    showRecentOutcomes,
    onRecentOutcomesChange,
  } = filters;

  const { onSearch, onClear, criteria, hasActiveSearch } = search;

  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(
    controlledSelectedStatuses ?? [],
  );

  useEffect(() => {
    if (controlledSelectedStatuses !== undefined) {
      setSelectedStatuses(controlledSelectedStatuses);
    }
  }, [controlledSelectedStatuses]);

  const debouncedStatusFilterChange = useMemo(
    () =>
      debounce((statuses: string[]) => {
        onStatusChange(statuses);
      }, 1000),
    [onStatusChange],
  );

  useEffect(() => {
    return () => {
      debouncedStatusFilterChange.cancel();
    };
  }, [debouncedStatusFilterChange]);

  const handleStatusChange = useCallback(
    (values: string[]) => {
      setSelectedStatuses(values);
      debouncedStatusFilterChange(values);
    },
    [debouncedStatusFilterChange],
  );

  const handleResetFilters = useCallback(() => {
    setSelectedStatuses([]);
    debouncedStatusFilterChange.cancel();
    onReset();
  }, [debouncedStatusFilterChange, onReset]);

  const handleSearchSubmit = useCallback(
    (searchCriteria: SearchCriteria) => {
      onSearch(searchCriteria);
      setIsSearchModalOpen(false);
    },
    [onSearch],
  );

  return (
    <div className={classNames(styles.searchBarContainer, className)}>
      <div className={styles.searchBar}>
        <div className={styles.filterSection}>
          <FontAwesomeIcon icon={faFilter} className={styles.filterIcon} />
          <StatusDropdown
            selectedStatuses={selectedStatuses}
            onStatusChange={handleStatusChange}
            availableStatuses={Object.values(AppealCurrentStatus)}
          />
          <button
            className={`${styles.filterButton} ${showReferredByMe ? styles.active : ""}`}
            onClick={() => onReferredByMeChange(!showReferredByMe)}
            type="button"
          >
            <FontAwesomeIcon icon={showReferredByMe ? faXmark : faPlus} />
            Referred by me
          </button>
          <DateRangeFilter
            dateRange={dateRange}
            onChange={onDateRangeChange}
            onClear={() => onDateRangeChange([null, null])}
          />
          <button
            className={`${styles.filterButton} ${showRecentOutcomes ? styles.active : ""}`}
            onClick={() => onRecentOutcomesChange(!showRecentOutcomes)}
            type="button"
          >
            <FontAwesomeIcon icon={showRecentOutcomes ? faXmark : faPlus} />
            Recently updated outcomes
          </button>
          <Button
            variant="text"
            size="2xs"
            onClick={handleResetFilters}
            className={styles.resetButton}
            disabled={
              selectedStatuses.length === 0 &&
              !showReferredByMe &&
              !showRecentOutcomes &&
              dateRange[0] === null &&
              dateRange[1] === null
            }
          >
            Reset Filters
          </Button>
        </div>

        <div className={styles.searchSection}>
          <button
            className={styles.searchButton}
            onClick={() => setIsSearchModalOpen(true)}
          >
            <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
            <span className={styles.searchButtonText}>Search</span>
          </button>
        </div>
      </div>

      {hasActiveSearch && (
        <div className={styles.searchChipsContainer}>
          <SearchChip searchCriteria={criteria} onRemove={onClear} />
        </div>
      )}

      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        onSearch={handleSearchSubmit}
        initialCriteria={criteria}
      />
    </div>
  );
};
